// Zod schemas for director structured outputs — strict on persistence-affecting fields.
import { z } from 'zod';
import { DIRECTOR_OUTPUT_SCHEMA_VERSION } from './errors.js';

const boundedText = (max: number) => z.string().min(1).max(max);

const approvedControlTypes = z.enum([
  'single_select',
  'multi_select',
  'short_text',
  'long_text',
  'time_range',
  'day_selection',
  'slider',
  'swatch',
  'review_card',
]);

export const QuestionOptionSchema = z.object({
  value: boundedText(200),
  label: boundedText(300),
});

export const ConnectedQuestionSchema = z
  .object({
    questionId: boundedText(120),
    schemaVersion: z.literal(DIRECTOR_OUTPUT_SCHEMA_VERSION),
    controlType: approvedControlTypes,
    prompt: boundedText(2000),
    whyItMatters: boundedText(500),
    dependencies: z.array(boundedText(120)),
    required: z.boolean(),
    validationRule: boundedText(500).optional(),
    options: z.array(QuestionOptionSchema).max(20).optional(),
    allowsNotSure: z.boolean().optional(),
  })
  .superRefine((q, ctx) => {
    if (!q.required && q.allowsNotSure !== true) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        message: 'Nonessential questions must set allowsNotSure: true',
      });
    }
  });

/** At most three dependency-ordered questions. */
export const ConnectedQuestionsOutputSchema = z
  .object({
    schemaVersion: z.literal(DIRECTOR_OUTPUT_SCHEMA_VERSION),
    questions: z.array(ConnectedQuestionSchema).max(3),
    fallbackQuestionIds: z.array(boundedText(120)).max(10).optional(),
  })
  .strict();

export const AssumptionSuggestionSchema = z.object({
  value: z.unknown(),
  assumption: boundedText(2000),
  confidence: z.enum(['high', 'medium', 'low']),
  contextSourceIds: z.array(boundedText(120)),
  requiresNewQuestion: z.boolean().optional(),
  newQuestionHint: boundedText(500).optional(),
});

export const AssumptionOutputSchema = z
  .object({
    schemaVersion: z.literal(DIRECTOR_OUTPUT_SCHEMA_VERSION),
    fieldPath: boundedText(200),
    primary: AssumptionSuggestionSchema,
    alternatives: z.array(AssumptionSuggestionSchema).max(2),
  })
  .strict();

export const WorldDeviceOutputItemSchema = z.object({
  deviceId: boundedText(80),
  category: boundedText(80),
  title: boundedText(200),
  description: boundedText(2000),
  trigger: boundedText(500),
  eligibleActorIds: z.array(boundedText(80)),
  consequenceRange: boundedText(200),
  intensity: z.enum(['mild', 'moderate', 'intense']),
  contextSourceIds: z.array(boundedText(120)),
  safetyStatus: z.enum(['reviewed', 'pending']).optional(),
});

export const WorldDevicesOutputSchema = z
  .object({
    schemaVersion: z.literal(DIRECTOR_OUTPUT_SCHEMA_VERSION),
    devices: z.array(WorldDeviceOutputItemSchema).length(20),
    generationSeed: boundedText(120),
  })
  .strict()
  .superRefine((val, ctx) => {
    const ids = val.devices.map((d) => d.deviceId);
    const unique = new Set(ids);
    if (unique.size !== ids.length) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        message: 'deviceId values must be unique',
      });
    }
  });

export const AppearanceSlotSchema = z.object({
  slot: z.enum(['hair', 'fur', 'color', 'top', 'bottom', 'shoes']),
  label: boundedText(120),
  description: boundedText(500),
});

export const TraitOptionSchema = z.object({
  traitKey: boundedText(80),
  label: boundedText(120),
  strength: z.number().min(0).max(10),
  reasonHint: boundedText(100).optional(),
});

export const CharacterOptionsOutputSchema = z
  .object({
    schemaVersion: z.literal(DIRECTOR_OUTPUT_SCHEMA_VERSION),
    appearanceOptions: z.array(AppearanceSlotSchema).min(1).max(12),
    traitOptions: z.array(TraitOptionSchema).max(5).optional(),
    preservedFields: z.array(boundedText(120)),
  })
  .strict();

export const RuntimeDialogueLineSchema = z.object({
  actorId: boundedText(80),
  text: boundedText(280),
});

export const RuntimeEventCandidateSchema = z.object({
  eventId: boundedText(80).optional(),
  summary: boundedText(500),
  actorIds: z.array(boundedText(80)),
  deviceIds: z.array(boundedText(80)).optional(),
  proposed: z.boolean(),
});

export const RuntimeActorEffectSchema = z.object({
  actorId: boundedText(80),
  mood: boundedText(120).optional(),
  memoryUpdate: boundedText(500).optional(),
});

export const RuntimeSceneOutputSchema = z
  .object({
    schemaVersion: z.literal(DIRECTOR_OUTPUT_SCHEMA_VERSION),
    dialogue: z.array(RuntimeDialogueLineSchema).max(40),
    eventCandidates: z.array(RuntimeEventCandidateSchema).max(10),
    actorEffects: z.array(RuntimeActorEffectSchema).max(20),
    summaryText: boundedText(2000),
    noPersistence: z.boolean().optional(),
  })
  .strict();

export const SummaryOutputSchema = z
  .object({
    schemaVersion: z.literal(DIRECTOR_OUTPUT_SCHEMA_VERSION),
    observedFacts: boundedText(4000),
    directorInterpretation: boundedText(2000).optional(),
    eventIds: z.array(boundedText(80)),
    priorSummaryHash: boundedText(64).optional(),
  })
  .strict();

export type ConnectedQuestionsOutput = z.infer<typeof ConnectedQuestionsOutputSchema>;
export type AssumptionOutput = z.infer<typeof AssumptionOutputSchema>;
export type WorldDevicesOutput = z.infer<typeof WorldDevicesOutputSchema>;
export type CharacterOptionsOutput = z.infer<typeof CharacterOptionsOutputSchema>;
export type RuntimeSceneOutput = z.infer<typeof RuntimeSceneOutputSchema>;
export type SummaryOutput = z.infer<typeof SummaryOutputSchema>;

/** @deprecated Prefer validateDirectorOutput for structured recoverable errors. */
export function validateStructuredOutput<T>(
  schema: z.ZodType<T>,
  raw: unknown,
): { ok: true; data: T } | { ok: false; error: string } {
  const result = schema.safeParse(raw);
  if (result.success) return { ok: true, data: result.data };
  return { ok: false, error: result.error.issues.map((i) => i.message).join('; ') };
}
